import React, { createContext, useState, useEffect, useContext } from "react"

/**
 * The visitor's city, decided once and remembered:
 *
 * 1. A city saved in localStorage (from an earlier visit) is final — we never
 *    ask for location or show the city picker again.
 * 2. First visit: ask the browser for the visitor's location (GPS/Wi-Fi) and
 *    map it to a city with /spaces/cityforlocation.
 * 3. If location is denied, unavailable, or can't be mapped to a city, flag
 *    `needsCityPicker` so the footer shows the "Select Your City" modal.
 * 4. Choosing a city or dismissing the modal (→ Bengaluru) saves it via
 *    updateLocation(), which ends the flow for future visits.
 *
 * Landing on a city or locality page (e.g. /office-spaces/chennai/) counts
 * as choosing that city: the page calls rememberCity(), which saves it, so
 * no location prompt or picker is shown. Later pages in other cities update
 * it the same way.
 *
 * Crawlers and lab tools (Lighthouse) get Bengaluru for this page view
 * without a prompt or modal, and nothing is saved for them.
 */

export const DEFAULT_CITY = { city: "Bengaluru", slug: "bengaluru" }

const BOT_UA =
  /bot|crawler|spider|crawling|lighthouse|headlesschrome|pagespeed/i

// cityforlocation answers "Chennai" for anywhere outside India, so only
// trust it for coordinates inside India's bounding box.
const inIndia = (lat, lng) => lat > 6 && lat < 37.5 && lng > 68 && lng < 97.5

const slugify = name => String(name).toLowerCase().trim().replace(/\s+/g, "-")

const LocationContext = createContext({
  ...DEFAULT_CITY,
  updateLocation: () => {},
  needsCityPicker: false,
  geoCoords: null,
})

/**
 * Save the city a page is about (city hubs, locality pages). Works outside
 * React components and before the provider has mounted; the provider picks
 * it up via the "cityChanged" event or on mount. No-op if unchanged.
 */
export function rememberCity(cityName, citySlug) {
  if (!cityName || !citySlug || typeof window === "undefined") return
  try {
    if (
      localStorage.getItem("city") === cityName &&
      localStorage.getItem("slug") === citySlug
    )
      return
    localStorage.setItem("city", cityName)
    localStorage.setItem("slug", citySlug)
  } catch (e) {
    // storage blocked: still tell the provider for this page view
  }
  window.dispatchEvent(
    new CustomEvent("cityChanged", { detail: { cityName, slug: citySlug } }),
  )
}

function readStoredCity() {
  try {
    const city = localStorage.getItem("city")
    const slug = localStorage.getItem("slug")
    return city && slug ? { city, slug } : null
  } catch (e) {
    return null
  }
}

// Keep the permission prompt (and the city API call) out of the page's
// loading work.
function afterLoadAndIdle() {
  return new Promise(resolve => {
    const idle = () =>
      window.requestIdleCallback
        ? window.requestIdleCallback(resolve, { timeout: 3000 })
        : setTimeout(resolve, 1000)
    if (document.readyState === "complete") idle()
    else window.addEventListener("load", idle, { once: true })
  })
}

// One lookup per page load, shared if the provider mounts more than once
// (it does during hydration), so the visitor is never asked twice.
let detection = null

function detectCity() {
  if (detection) return detection
  detection = afterLoadAndIdle().then(
    () =>
      new Promise(resolve => {
        const fail = coords => resolve({ coords, city: null })
        if (!navigator.geolocation) return fail(null)
        navigator.geolocation.getCurrentPosition(
          pos => {
            const coords = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }
            if (!inIndia(coords.lat, coords.lng)) return fail(coords)
            fetch(
              `https://gofloaters.web.app/spaces/cityforlocation/?lat=${coords.lat}&lng=${coords.lng}`,
            )
              .then(res => res.json())
              .then(data =>
                data && data.status === "success" && data.city
                  ? resolve({ coords, city: data.city })
                  : fail(coords),
              )
              .catch(() => fail(coords))
          },
          () => fail(null),
          // timeout only starts once the visitor has answered the permission
          // prompt; a cached fix up to an hour old is fine for picking a city.
          { timeout: 15000, maximumAge: 60 * 60 * 1000 },
        )
      }),
  )
  return detection
}

export const LocationProvider = ({ children }) => {
  const [city, setCity] = useState(DEFAULT_CITY.city)
  const [slug, setSlug] = useState(DEFAULT_CITY.slug)
  const [needsCityPicker, setNeedsCityPicker] = useState(false)
  // Coordinates from this session's geolocation (first visit only), for Ria.
  const [geoCoords, setGeoCoords] = useState(null)

  const updateLocation = React.useCallback((cityName, citySlug) => {
    setCity(cityName)
    setSlug(citySlug)
    setNeedsCityPicker(false)
    try {
      localStorage.setItem("city", cityName)
      localStorage.setItem("slug", citySlug)
    } catch (e) {
      // storage blocked (private mode): the choice lasts for this page view
    }
    // For legacy components that still listen for this event.
    window.dispatchEvent(
      new CustomEvent("cityChanged", {
        detail: { cityName, slug: citySlug },
      }),
    )
  }, [])

  // Other components (or tabs) changing the city.
  useEffect(() => {
    const handleCityChange = e => {
      if (e.detail && e.detail.cityName) setCity(e.detail.cityName)
      if (e.detail && e.detail.slug) setSlug(e.detail.slug)
      // A city was chosen (picker, header, or a city page): stop asking.
      setNeedsCityPicker(false)
    }
    window.addEventListener("cityChanged", handleCityChange)
    return () => window.removeEventListener("cityChanged", handleCityChange)
  }, [])

  // First-visit resolution — runs once per page load, but only does work
  // when no city has been saved yet.
  useEffect(() => {
    const stored = readStoredCity()
    if (stored) {
      setCity(stored.city)
      setSlug(stored.slug)
      return undefined
    }
    if (BOT_UA.test(navigator.userAgent)) return undefined
    let cancelled = false
    detectCity().then(({ coords, city: detected }) => {
      if (cancelled || readStoredCity()) return
      if (coords) setGeoCoords(coords)
      if (detected) updateLocation(detected, slugify(detected))
      else setNeedsCityPicker(true)
    })
    return () => {
      cancelled = true
    }
  }, [updateLocation])

  return (
    <LocationContext.Provider
      value={{ city, slug, updateLocation, needsCityPicker, geoCoords }}
    >
      {children}
    </LocationContext.Provider>
  )
}

export const useLocation = () => useContext(LocationContext)

export default LocationContext
